import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { classes, style } from 'typestyle';
import DatePicker from 'react-date-picker';
import { type Value } from 'react-date-picker/dist/cjs/shared/types';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import Plot from 'react-plotly.js';
import {
  endOfDay,
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  endOfYear,
  format,
  startOfDay,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
} from 'date-fns';
import * as tabStyles from '../statistics-tabs.styles';
import * as styles from './reporting-tab.styles';
import DashboardTheme from '../dashboard-theme';
import { IsMobileContext } from '../../../../core/context/is-mobile';
import { Center } from '../../../../core/components/center/center.component';
import ActivityIndicator from '../../../../core/components/activity-indicator/activity-indicator.component';
import PrimaryButton from '../../../../core/components/buttons/primary-button.component';
import notify from '../../../../core/components/notify/notify.component';
import StyleConstants from '../../../../core/styles.constants';
import { useKeycloak } from '../../../authentication/keycloak/KeycloakProvider';
import { useDashboardReporting, useEmployeePresence } from '../hooks/dashboard.hook';
import {
  getEmployeePresenceCsvExport,
  getEmployeePresenceDayRows,
  getReportingCsvExport,
  type ReportingFilter,
} from '../services/dashboard.service';
import EmployeePresenceDetailModal from '../modals/employee-presence-detail.modal.component';
import { ReportingKpiRow } from '../components/reporting-kpi-row.component';
import GroupReportTable from '../components/group-report-table.component';
import { barChartLabels, leftAlignedLabels } from '../components/chart-labels';
import {
  type EmployeePresence,
  type EmployeePresenceDayRow,
  type GroupReport,
  type OfficeReportBreakdown,
  type ReportingComparisonMode,
  type ReportingKpis,
  type RoomReport,
  type TrendPoint,
} from '../models/dashboard.models';
import { usePagedRows, useSortableRows } from '../components/table-controls.hook';
import SortableHeaderCell from '../components/sortable-header-cell.component';
import TablePagination from '../components/table-pagination.component';
import { useCompanies } from '../../../companies/hooks/companies.hook';
import { useOfficeManagerOrAdminOffices } from '../../../offices/hooks/offices.hook';
import AppConstants from '../../../../core/app.constants';
import plotlyChartConfig from '../components/plotly-chart-config';

const ROOMS_ROWS_PER_PAGE = 8;
const EMPLOYEES_ROWS_PER_PAGE = 8;
const EMPLOYEE_CHART_MAX_BARS = 15;
// A print window builds the whole table as DOM before it can paginate it. Seven months of one real
// customer is ~20k employee-day rows, which is well past what a browser will render comfortably —
// beyond this we refuse and point at the CSV rather than hanging the tab.
const EMPLOYEE_PDF_MAX_ROWS = 5000;
const OFFICE_COMPARISON_TREND_COLORS = [
  DashboardTheme.navy700,
  DashboardTheme.amber500,
  DashboardTheme.teal500,
  DashboardTheme.green600,
  DashboardTheme.red500,
];

// Plotly's default hover tooltip is a near-transparent white box with dark text — unreadable
// against light chart backgrounds and especially against the filled/shaded traces used here.
// Every chart below uses this same solid, high-contrast tooltip styling.
const PLOTLY_HOVERLABEL = {
  bgcolor: DashboardTheme.navy900,
  bordercolor: DashboardTheme.navy900,
  font: { color: DashboardTheme.white, family: DashboardTheme.bodyFontFamily, size: 12 },
};

interface Props {
  readonly selectedOfficeId?: string;
}

type PeriodPreset = 'today' | 'thisWeek' | 'thisMonth' | 'thisQuarter' | 'thisYear' | 'custom';

const PRESETS: PeriodPreset[] = ['today', 'thisWeek', 'thisMonth', 'thisQuarter', 'thisYear', 'custom'];
const COMPARISON_MODES: ReportingComparisonMode[] = ['None', 'PreviousPeriod', 'SamePeriodLastYear'];
const DATE_FORMAT = 'yyyy-MM-dd';

const getPresetRange = (preset: PeriodPreset, customStart?: Date, customEnd?: Date): { start: Date; end: Date } => {
  const now = new Date();

  switch (preset) {
    case 'today':
      return { start: startOfDay(now), end: endOfDay(now) };
    case 'thisWeek':
      return { start: startOfWeek(now, { weekStartsOn: 1 }), end: endOfWeek(now, { weekStartsOn: 1 }) };
    case 'thisMonth':
      return { start: startOfMonth(now), end: endOfMonth(now) };
    case 'thisQuarter':
      return { start: startOfQuarter(now), end: endOfQuarter(now) };
    case 'thisYear':
      return { start: startOfYear(now), end: endOfYear(now) };
    case 'custom':
    default:
      return {
        start: customStart ?? startOfWeek(now, { weekStartsOn: 1 }),
        end: customEnd ?? endOfWeek(now, { weekStartsOn: 1 }),
      };
  }
};

const comparisonModeKey = (mode: ReportingComparisonMode): string => {
  if (mode === 'PreviousPeriod') {
    return 'reporting-comparison-previous-period';
  }
  if (mode === 'SamePeriodLastYear') {
    return 'reporting-comparison-same-period-last-year';
  }
  return 'reporting-comparison-none';
};

const HTML_ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

/// <summary>The print HTML below is written into a `window.open('', '_blank')` document, which
/// inherits this app's origin — anything unescaped there runs as us. Room, office and person names
/// are all user-editable (an employee can set their own first/last name), so every interpolated
/// value has to go through this.</summary>
const escapeHtml = (value: string | number): string =>
  String(value).replace(/[&<>"']/g, (character) => HTML_ESCAPES[character]);

const PRINT_STYLES = `
  body { font-family: Arial, sans-serif; color: #26459C; padding: 24px; }
  h1 { font-size: 20px; }
  h2 { font-size: 14px; color: #575756; font-weight: normal; }
  h3 { font-size: 15px; margin-top: 28px; }
  .kpi-row { display: flex; gap: 16px; margin: 20px 0; }
  .kpi-card { border: 1px solid #ccc; border-radius: 8px; padding: 12px 16px; flex: 1; }
  .kpi-label { font-size: 11px; color: #575756; margin-bottom: 6px; }
  .kpi-value { font-size: 22px; font-weight: bold; }
  .kpi-total { font-size: 13px; font-weight: normal; color: #575756; }
  table { width: 100%; border-collapse: collapse; margin-top: 20px; }
  th, td { text-align: left; padding: 8px 10px; border-bottom: 1px solid #ddd; font-size: 12px; }
  th { color: #575756; }`;

/// <summary>The employee-presence table shared by the main report PDF and the panel-scoped one:
/// one row per employee per office per day, with the room and parking booked for each day-part —
/// the same shape the CSV exports. Returns an empty string when there is nothing to print, so the
/// caller can concatenate it unconditionally.</summary>
const buildEmployeeSectionHtml = (days: EmployeePresenceDayRow[], labels: Record<string, string>): string => {
  if (days.length === 0) {
    return '';
  }

  const dayRows = days
    .map(
      (day) => `
        <tr>
          <td>${escapeHtml(day.office)}</td>
          <td>${escapeHtml(day.fullName)}</td>
          <td>${escapeHtml(day.email)}</td>
          <td>${escapeHtml(day.date)}</td>
          <td>${escapeHtml(day.occupationMorning)}</td>
          <td>${escapeHtml(day.occupationAfternoon)}</td>
          <td>${escapeHtml(day.parkingMorning)}</td>
          <td>${escapeHtml(day.parkingAfternoon)}</td>
          <td>${escapeHtml(day.isCheckedIn === 'true' ? labels.yes : labels.no)}</td>
        </tr>`,
    )
    .join('');

  return `
    <h3>${escapeHtml(labels.employeePresence)}</h3>
    <table>
      <thead>
        <tr>
          <th>${escapeHtml(labels.office)}</th>
          <th>${escapeHtml(labels.employeeName)}</th>
          <th>${escapeHtml(labels.employeeEmail)}</th>
          <th>${escapeHtml(labels.date)}</th>
          <th>${escapeHtml(labels.occupationMorning)}</th>
          <th>${escapeHtml(labels.occupationAfternoon)}</th>
          <th>${escapeHtml(labels.parkingMorning)}</th>
          <th>${escapeHtml(labels.parkingAfternoon)}</th>
          <th>${escapeHtml(labels.checkedIn)}</th>
        </tr>
      </thead>
      <tbody>
        ${dayRows}
      </tbody>
    </table>`;
};

const buildEmployeePrintHtml = (title: string, periodLabel: string, employeeSection: string): string => `
    <!DOCTYPE html>
    <html>
      <head>
        <title>${escapeHtml(title)}</title>
        <meta charset="utf-8" />
        <style>${PRINT_STYLES}</style>
      </head>
      <body>
        <h1>${escapeHtml(title)}</h1>
        <h2>${escapeHtml(periodLabel)}</h2>
        ${employeeSection}
      </body>
    </html>`;

/** The four KPI labels, shared by every KPI row in the printed report. */
interface KpiPrintLabels {
  avgOccupancy: string;
  reservations: string;
  noShows: string;
  noShowRate: string;
}

interface ReportPrintLabels extends KpiPrintLabels {
  room: string;
  office: string;
  checkIns: string;
  /** Heading above the overall KPIs; only printed when a group section follows. */
  allReservations: string;
}

interface GroupPrintLabels extends KpiPrintLabels {
  office: string;
  checkIns: string;
  group: string;
  groupReservations: string;
  deleted: string;
}

/** " / total" after a printed KPI value, when the row is a part of the totals (the group row). */
const printTotal = (total: string | undefined): string => (total === undefined ? '' : `<span class="kpi-total"> / ${total}</span>`);

const formatPercent = (value: number): string => `${value.toFixed(1)}%`;

const buildKpiCardsHtml = (kpis: ReportingKpis, labels: KpiPrintLabels, totals?: ReportingKpis): string => {
  // Counts only: the percentages are the row's own figure, as on screen.
  const reservationsTotal = printTotal(totals && String(totals.reservations));
  const noShowsTotal = printTotal(totals && String(totals.noShows));

  return `
        <div class="kpi-row">
          <div class="kpi-card">
            <div class="kpi-label">${escapeHtml(labels.avgOccupancy)}</div>
            <div class="kpi-value">${formatPercent(kpis.averageOccupancyPercentage)}</div>
          </div>
          <div class="kpi-card">
            <div class="kpi-label">${escapeHtml(labels.reservations)}</div>
            <div class="kpi-value">${kpis.reservations}${reservationsTotal}</div>
          </div>
          <div class="kpi-card">
            <div class="kpi-label">${escapeHtml(labels.noShows)}</div>
            <div class="kpi-value">${kpis.noShows}${noShowsTotal}</div>
          </div>
          <div class="kpi-card">
            <div class="kpi-label">${escapeHtml(labels.noShowRate)}</div>
            <div class="kpi-value">${formatPercent(kpis.noShowRatePercentage)}</div>
          </div>
        </div>`;
};

/// <summary>The group KPIs and the per-group table for the printed report; empty when the API sent none.</summary>
const buildGroupPrintSection = (
  groupKpis: ReportingKpis | undefined,
  allKpis: ReportingKpis,
  groups: GroupReport[] | undefined,
  showOfficeColumn: boolean,
  labels: GroupPrintLabels,
): string => {
  if (!groupKpis) {
    return '';
  }

  const groupRows = (groups ?? [])
    .map(
      (group) => `
        <tr>
          <td>${escapeHtml(group.groupName)}${group.isGroupDeleted ? ` (${escapeHtml(labels.deleted)})` : ''}</td>
          ${showOfficeColumn ? `<td>${escapeHtml(group.officeName)}</td>` : ''}
          <td>${group.averageOccupancyPercentage.toFixed(1)}%</td>
          <td>${group.reservations}</td>
          <td>${group.checkIns}</td>
          <td>${group.noShows}</td>
          <td>${group.noShowRatePercentage.toFixed(1)}%</td>
        </tr>`,
    )
    .join('');

  const groupTable = groupRows
    ? `
        <table>
          <thead>
            <tr>
              <th>${escapeHtml(labels.group)}</th>
              ${showOfficeColumn ? `<th>${escapeHtml(labels.office)}</th>` : ''}
              <th>${escapeHtml(labels.avgOccupancy)}</th>
              <th>${escapeHtml(labels.reservations)}</th>
              <th>${escapeHtml(labels.checkIns)}</th>
              <th>${escapeHtml(labels.noShows)}</th>
              <th>${escapeHtml(labels.noShowRate)}</th>
            </tr>
          </thead>
          <tbody>${groupRows}</tbody>
        </table>`
    : '';

  return `
        <h3>${escapeHtml(labels.groupReservations)}</h3>
        ${buildKpiCardsHtml(groupKpis, labels, allKpis)}
        ${groupTable}`;
};

const buildPrintHtml = (
  title: string,
  periodLabel: string,
  kpis: ReportingKpis,
  rooms: RoomReport[],
  showOfficeColumn: boolean,
  labels: ReportPrintLabels,
  employeeSection = '',
  groupSection = '',
): string => {
  const roomRows = rooms
    .map(
      (room) => `
        <tr>
          <td>${escapeHtml(room.roomName)}</td>
          ${showOfficeColumn ? `<td>${escapeHtml(room.officeName)}</td>` : ''}
          <td>${room.averageOccupancyPercentage.toFixed(1)}%</td>
          <td>${room.reservations}</td>
          <td>${room.checkIns}</td>
          <td>${room.noShows}</td>
          <td>${room.noShowRatePercentage.toFixed(1)}%</td>
        </tr>`,
    )
    .join('');

  return `
    <!DOCTYPE html>
    <html>
      <head>
        <title>${escapeHtml(title)}</title>
        <meta charset="utf-8" />
        <style>${PRINT_STYLES}</style>
      </head>
      <body>
        <h1>${escapeHtml(title)}</h1>
        <h2>${escapeHtml(periodLabel)}</h2>
        ${groupSection ? `<h3>${escapeHtml(labels.allReservations)}</h3>` : ''}
        ${buildKpiCardsHtml(kpis, labels)}
        ${groupSection}
        <table>
          <thead>
            <tr>
              <th>${escapeHtml(labels.room)}</th>
              ${showOfficeColumn ? `<th>${escapeHtml(labels.office)}</th>` : ''}
              <th>${escapeHtml(labels.avgOccupancy)}</th>
              <th>${escapeHtml(labels.reservations)}</th>
              <th>${escapeHtml(labels.checkIns)}</th>
              <th>${escapeHtml(labels.noShows)}</th>
              <th>${escapeHtml(labels.noShowRate)}</th>
            </tr>
          </thead>
          <tbody>
            ${roomRows}
          </tbody>
        </table>
        ${employeeSection}
      </body>
    </html>`;
};

const ReportingTab = ({ selectedOfficeId }: Props): React.JSX.Element => {
  const { t } = useTranslation();
  const isMobile = useContext(IsMobileContext);
  const keycloak = useKeycloak();

  const [preset, setPreset] = useState<PeriodPreset>('thisWeek');
  const [customStart, setCustomStart] = useState<Date | undefined>();
  const [customEnd, setCustomEnd] = useState<Date | undefined>();
  const [comparison, setComparison] = useState<ReportingComparisonMode>('None');
  const [isExportingCsv, setIsExportingCsv] = useState<boolean>(false);

  const isWhyellow = keycloak.hasRealmRole(AppConstants.WHYELLOW_ROLE);
  const { companies } = useCompanies();
  const { offices } = useOfficeManagerOrAdminOffices();

  const [compareMode, setCompareMode] = useState<boolean>(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | undefined>(undefined);
  const [selectedOfficeIds, setSelectedOfficeIds] = useState<string[]>([]);
  // Single-office / all-offices selection for the non-comparison view. Defaults to whatever
  // office is selected elsewhere in the page (e.g. via Office detail), but is independently
  // changeable here — reporting for "all offices" or a specific one shouldn't require leaving
  // this tab.
  const [reportOfficeId, setReportOfficeId] = useState<string | undefined>(selectedOfficeId);

  // The office-manager/admin office list (`useOfficeManagerOrAdminOffices`) doesn't carry a
  // companyId per office, so it can't be filtered by company client-side. When a company is
  // selected, source the candidate offices from that company's own `offices` list instead (from
  // `useCompanies`, which already scopes them correctly) rather than filtering the wrong list
  // down to nothing.
  const selectedCompany = useMemo(() => companies?.find((company) => company.id === selectedCompanyId), [companies, selectedCompanyId]);
  const compareCandidateOffices = useMemo(() => {
    const candidates = selectedCompanyId ? selectedCompany?.offices ?? [] : offices ?? [];
    return [...candidates].sort((a, b) => a.name.localeCompare(b.name));
  }, [selectedCompanyId, selectedCompany, offices]);

  const toggleCompareOffice = useCallback((officeId: string): void => {
    setSelectedOfficeIds((current) =>
      (current.includes(officeId) ? current.filter((id) => id !== officeId) : [...current, officeId]));
  }, []);

  useEffect(() => {
    if (selectedOfficeId) {
      setReportOfficeId(selectedOfficeId);
    }
  }, [selectedOfficeId]);

  // Switching company can make the currently-picked single office invalid (it may belong to a
  // different company) — fall back to "All offices" (scoped to the new company) rather than
  // silently keeping a selection that no longer matches. Skip on first mount so an office passed
  // in via `selectedOfficeId` isn't immediately cleared.
  const isFirstCompanyRender = useRef(true);
  useEffect(() => {
    if (isFirstCompanyRender.current) {
      isFirstCompanyRender.current = false;
      return;
    }
    setReportOfficeId(undefined);
  }, [selectedCompanyId]);

  const { start, end } = useMemo(() => getPresetRange(preset, customStart, customEnd), [preset, customStart, customEnd]);
  const startDate = useMemo(() => format(start, DATE_FORMAT), [start]);
  const endDate = useMemo(() => format(end, DATE_FORMAT), [end]);
  // A custom range where the end date was picked before the start date is otherwise silently
  // sent to the backend, which rejects it with a 400 — catch it client-side instead, with a
  // message that actually explains what's wrong, and block export before even trying.
  const isCustomRangeInvalid = preset === 'custom' && customStart !== undefined && customEnd !== undefined && customEnd < customStart;

  const reportingFilter = useMemo<ReportingFilter>(() => {
    if (compareMode && selectedOfficeIds.length >= 2) {
      return { officeIds: selectedOfficeIds, compareOffices: true, companyId: selectedCompanyId };
    }
    return { officeId: reportOfficeId, companyId: isWhyellow ? selectedCompanyId : undefined };
  }, [compareMode, selectedOfficeIds, selectedCompanyId, reportOfficeId, isWhyellow]);

  const { initialized, reporting, error, errorStatus } = useDashboardReporting(startDate, endDate, comparison, reportingFilter);

  // Employee presence uses the same office/company scoping as the main report (including
  // multi-office compare selections) and, since the panel was unified with the rest of the tab,
  // the same period selector at the top — "how many days was each employee actually checked in",
  // the modern equivalent of the old attendance feature.
  const [employeeSearchQuery, setEmployeeSearchQuery] = useState<string>('');
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeePresence | undefined>();
  const [isExportingEmployeeCsv, setIsExportingEmployeeCsv] = useState<boolean>(false);
  const [isExportingPdf, setIsExportingPdf] = useState<boolean>(false);

  const presenceFilter = useMemo<ReportingFilter>(() => {
    if (compareMode && selectedOfficeIds.length >= 2) {
      return { officeIds: selectedOfficeIds, companyId: selectedCompanyId };
    }
    return { officeId: reportOfficeId, companyId: isWhyellow ? selectedCompanyId : undefined };
  }, [compareMode, selectedOfficeIds, selectedCompanyId, reportOfficeId, isWhyellow]);

  // An end-before-start custom range is rejected by the backend with a 400; the tab already says so
  // inline above, so don't fire the request at all rather than surfacing a second, vaguer error.
  const { initialized: employeesInitialized, employees, error: employeesError } = useEmployeePresence(
    startDate,
    endDate,
    presenceFilter,
    !isCustomRangeInvalid,
  );

  const filteredEmployees = useMemo(() => {
    const trimmedQuery = employeeSearchQuery.trim().toLowerCase();
    if (!trimmedQuery) {
      return employees ?? [];
    }
    return (employees ?? []).filter(
      (employee) => employee.fullName.toLowerCase().includes(trimmedQuery) || employee.email.toLowerCase().includes(trimmedQuery),
    );
  }, [employees, employeeSearchQuery]);

  const {
    sortedRows: sortedEmployees,
    sortKey: employeesSortKey,
    sortDirection: employeesSortDirection,
    toggleSort: toggleEmployeesSort,
  } = useSortableRows<EmployeePresence>(
    filteredEmployees,
    (employee, key) => {
      // No 'office' case: the panel table doesn't show an Office column (a row is a person, who may
      // span several offices). The PDF export still prints one — see buildEmployeeSectionHtml.
      switch (key) {
        case 'email':
          return employee.email;
        case 'presentDays':
          return employee.presentDays;
        case 'fullName':
        default:
          return employee.fullName;
      }
    },
    'presentDays',
    'desc',
  );
  const {
    pagedRows: pagedEmployees,
    page: employeesPage,
    setPage: setEmployeesPage,
    pageCount: employeesPageCount,
  } = usePagedRows(sortedEmployees, EMPLOYEES_ROWS_PER_PAGE);

  const employeeChartRows = [...sortedEmployees]
    .sort((a, b) => b.presentDays - a.presentDays)
    .slice(0, EMPLOYEE_CHART_MAX_BARS)
    .reverse();

  const {
    sortedRows: sortedReportRooms,
    sortKey: roomsSortKey,
    sortDirection: roomsSortDirection,
    toggleSort: toggleRoomsSort,
  } = useSortableRows<RoomReport>(reporting?.rooms ?? [], (room, key) => {
    switch (key) {
      case 'office':
        return room.officeName;
      case 'avgOccupancy':
        return room.averageOccupancyPercentage;
      case 'reservations':
        return room.reservations;
      case 'checkIns':
        return room.checkIns;
      case 'noShows':
        return room.noShows;
      case 'noShowRate':
        return room.noShowRatePercentage;
      case 'room':
      default:
        return room.roomName;
    }
  });
  const { pagedRows: pagedReportRooms, page: roomsPage, setPage: setRoomsPage, pageCount: roomsPageCount } = usePagedRows(
    sortedReportRooms,
    ROOMS_ROWS_PER_PAGE,
  );

  const employeePrintLabels = useMemo(
    () => ({
      employeePresence: t('reporting-employee-presence-title'),
      employeeName: t('reporting-employee-presence-name'),
      employeeEmail: t('reporting-employee-presence-email'),
      office: t('reporting-table-office'),
      date: t('reporting-employee-presence-detail-date'),
      occupationMorning: t('reporting-employee-presence-occupation-morning'),
      occupationAfternoon: t('reporting-employee-presence-occupation-afternoon'),
      parkingMorning: t('reporting-employee-presence-parking-morning'),
      parkingAfternoon: t('reporting-employee-presence-parking-afternoon'),
      checkedIn: t('reporting-employee-presence-detail-checked-in'),
      yes: t('reporting-employee-presence-yes'),
      no: t('reporting-employee-presence-no'),
    }),
    [t],
  );

  /// <summary>Fetches the per-day rows the PDF prints. Deliberately on demand rather than with the
  /// tab: this is one row per employee per office per day, which for a long period is far more data
  /// than the panel itself needs.</summary>
  const fetchEmployeeDayRows = useCallback(async (): Promise<EmployeePresenceDayRow[] | undefined> => {
    const rows = await getEmployeePresenceDayRows(startDate, endDate, keycloak, presenceFilter);
    if (!rows) {
      notify(t('reporting-export-pdf-error'));
      return undefined;
    }

    // Never truncate silently — a short PDF that looks complete is worse than no PDF.
    if (rows.length > EMPLOYEE_PDF_MAX_ROWS) {
      notify(t('reporting-export-pdf-too-large'));
      return undefined;
    }

    return rows;
  }, [startDate, endDate, keycloak, presenceFilter, t]);

  /// <summary>Opens a print window for whatever HTML the caller built, sharing the pop-up-blocked
  /// handling between the full report and the panel-scoped employee report.</summary>
  const openPrintWindow = useCallback(
    (html: string): void => {
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        notify(t('reporting-export-pdf-error'));
        return;
      }

      printWindow.document.open();
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.focus();
      printWindow.onload = (): void => {
        printWindow.print();
      };
    },
    [t],
  );

  const handleEmployeeCsvExport = useCallback(async (): Promise<void> => {
    if (isCustomRangeInvalid) {
      notify(t('reporting-invalid-period-message'));
      return;
    }

    if (!employees || employees.length === 0) {
      notify(t('reporting-export-no-data-message'));
      return;
    }

    setIsExportingEmployeeCsv(true);
    const blob = await getEmployeePresenceCsvExport(startDate, endDate, keycloak, presenceFilter);
    setIsExportingEmployeeCsv(false);

    if (!blob) {
      notify(t('reporting-export-csv-error'));
      return;
    }

    const element = document.createElement('a');
    element.href = URL.createObjectURL(blob);
    element.download = `employee_presence_${startDate}_${endDate}.csv`;
    document.body.appendChild(element);
    element.click();
    URL.revokeObjectURL(element.href);
    element.remove();
    notify(t('reporting-export-csv-success'), true);
  }, [keycloak, startDate, endDate, presenceFilter, isCustomRangeInvalid, employees, t]);

  const handleEmployeePdfExport = useCallback(async (): Promise<void> => {
    if (isCustomRangeInvalid) {
      notify(t('reporting-invalid-period-message'));
      return;
    }

    if (!employees || employees.length === 0) {
      notify(t('reporting-export-no-data-message'));
      return;
    }

    setIsExportingPdf(true);
    const dayRows = await fetchEmployeeDayRows();
    setIsExportingPdf(false);
    if (!dayRows) {
      return;
    }

    if (dayRows.length === 0) {
      notify(t('reporting-export-no-data-message'));
      return;
    }

    const section = buildEmployeeSectionHtml(dayRows, employeePrintLabels);
    openPrintWindow(buildEmployeePrintHtml(t('reporting-employee-presence-title'), `${startDate} — ${endDate}`, section));
  }, [employees, employeePrintLabels, startDate, endDate, isCustomRangeInvalid, fetchEmployeeDayRows, openPrintWindow, t]);

  const handleCsvExport = useCallback(async (): Promise<void> => {
    if (isCustomRangeInvalid) {
      notify(t('reporting-invalid-period-message'));
      return;
    }

    // Check the same "is there anything to export" signal the PDF export already uses (the
    // already-fetched report), before making a separate network call — otherwise CSV and PDF can
    // disagree about the same empty-data case: PDF reports it cleanly from local state, while CSV
    // only found out via a raw network response and fell back to a generic failure message.
    if (!reporting || reporting.rooms.length === 0) {
      notify(t('reporting-export-no-data-message'));
      return;
    }

    setIsExportingCsv(true);
    const blob = await getReportingCsvExport(startDate, endDate, keycloak, reportingFilter);
    setIsExportingCsv(false);

    if (!blob) {
      notify(t('reporting-export-csv-error'));
      return;
    }

    // The CSV always includes a header row even with zero data rows, so an empty export isn't an
    // empty blob — it's a blob containing only that header line.
    const csvText = await blob.text();
    if (csvText.trim().split('\n').length <= 1) {
      notify(t('reporting-export-no-data-message'));
      return;
    }

    const element = document.createElement('a');
    element.href = URL.createObjectURL(blob);
    element.download = `occupancy_report_${startDate}_${endDate}.csv`;
    document.body.appendChild(element);
    element.click();
    URL.revokeObjectURL(element.href);
    element.remove();
    notify(t('reporting-export-csv-success'), true);
  }, [keycloak, startDate, endDate, reportingFilter, isCustomRangeInvalid, reporting, t]);

  const handlePdfExport = useCallback(async (): Promise<void> => {
    if (isCustomRangeInvalid) {
      notify(t('reporting-invalid-period-message'));
      return;
    }

    if (!reporting || reporting.rooms.length === 0) {
      notify(t('reporting-export-no-data-message'));
      return;
    }

    // The report carries an employee-presence section. Printing while that panel is still fetching
    // would silently produce a PDF without it, which reads as "nobody was present".
    if (!employeesInitialized) {
      notify(t('reporting-export-still-loading'));
      return;
    }

    setIsExportingPdf(true);
    const dayRows = await fetchEmployeeDayRows();
    setIsExportingPdf(false);
    if (!dayRows) {
      return;
    }

    const showOfficeColumn = reportingFilter.officeId === undefined;
    const periodLabel = `${startDate} — ${endDate}`;
    const html = buildPrintHtml(
      t('reporting-print-title'),
      periodLabel,
      reporting.kpis,
      reporting.rooms,
      showOfficeColumn,
      {
        avgOccupancy: t('reporting-kpi-avg-occupancy'),
        reservations: t('reporting-kpi-reservations'),
        noShows: t('reporting-kpi-no-shows'),
        noShowRate: t('reporting-kpi-no-show-rate'),
        room: t('reporting-table-room'),
        office: t('reporting-table-office'),
        checkIns: t('reporting-table-checkins'),
        allReservations: t('reporting-kpis-all'),
      },
      buildEmployeeSectionHtml(dayRows, employeePrintLabels),
      buildGroupPrintSection(reporting.groupKpis, reporting.kpis, reporting.groups, showOfficeColumn, {
        avgOccupancy: t('reporting-kpi-avg-occupancy'),
        reservations: t('reporting-kpi-reservations'),
        noShows: t('reporting-kpi-no-shows'),
        noShowRate: t('reporting-kpi-no-show-rate'),
        office: t('reporting-table-office'),
        checkIns: t('reporting-table-checkins'),
        group: t('reporting-table-group'),
        groupReservations: t('reporting-kpis-groups'),
        deleted: t('reporting-group-deleted'),
      }),
    );

    openPrintWindow(html);
  }, [
    reporting,
    reportingFilter,
    employeesInitialized,
    employeePrintLabels,
    startDate,
    endDate,
    isCustomRangeInvalid,
    fetchEmployeeDayRows,
    openPrintWindow,
    t,
  ]);

  const showOfficeColumn = reportingFilter.officeId === undefined;
  // Group columns in the office comparison only when the API sends them.
  const hasOfficeGroupKpis = reporting?.officeBreakdowns?.some((office) => office.groupKpis !== undefined) ?? false;

  const sortedRooms = reporting ? [...reporting.rooms].sort((a, b) => b.noShows - a.noShows) : [];
  const topNoShowRooms = sortedRooms.slice(0, 10).reverse();

  const trendTraces: Plotly.Data[] = reporting
    ? [
      {
        x: reporting.occupancyTrend.map((_point: TrendPoint, index: number) => index),
        y: reporting.occupancyTrend.map((point: TrendPoint) => point.occupancyPercentage),
        text: reporting.occupancyTrend.map((point: TrendPoint) => point.date),
        hovertemplate: '%{text}<br>%{y:.1f}%<extra></extra>',
        type: 'scatter',
        mode: 'lines+markers',
        name: t('reporting-trend-series-current').toString(),
        line: { color: DashboardTheme.navy700, shape: 'spline', width: 2.5 },
        marker: { size: 5, color: DashboardTheme.navy700 },
        fill: 'tozeroy',
        fillcolor: 'rgba(40, 74, 114, 0.08)',
      },
    ]
    : [];

  if (reporting?.comparisonOccupancyTrend) {
    trendTraces.push({
      x: reporting.comparisonOccupancyTrend.map((_point: TrendPoint, index: number) => index),
      y: reporting.comparisonOccupancyTrend.map((point: TrendPoint) => point.occupancyPercentage),
      text: reporting.comparisonOccupancyTrend.map((point: TrendPoint) => point.date),
      hovertemplate: '%{text}<br>%{y:.1f}%<extra></extra>',
      type: 'scatter',
      mode: 'lines+markers',
      name: t('reporting-trend-series-comparison').toString(),
      line: { color: DashboardTheme.amber500, shape: 'spline', width: 2, dash: 'dash' },
      marker: { size: 5, color: DashboardTheme.amber500 },
    });
  }

  // "Presence" — reserved vs. actually checked-in headcount per day, the modern equivalent of
  // the old feature's weekly-average attendance chart, using data the reporting query already
  // returns per day (no new backend endpoint needed).
  const presenceTraces: Plotly.Data[] = reporting
    ? [
      {
        x: reporting.occupancyTrend.map((point: TrendPoint) => point.date),
        y: reporting.occupancyTrend.map((point: TrendPoint) => point.reservations),
        type: 'bar',
        name: t('reporting-presence-series-reservations').toString(),
        marker: { color: DashboardTheme.gray300 },
        hovertemplate: '%{x}<br>%{y}<extra></extra>',
      },
      {
        x: reporting.occupancyTrend.map((point: TrendPoint) => point.date),
        y: reporting.occupancyTrend.map((point: TrendPoint) => point.checkIns),
        type: 'bar',
        name: t('reporting-presence-series-checkins').toString(),
        marker: { color: DashboardTheme.green600 },
        hovertemplate: '%{x}<br>%{y}<extra></extra>',
      },
    ]
    : [];

  // Shortened, de-duplicated labels for the bars; the full room and office name stay in the hover.
  const noShowLabels = barChartLabels(
    topNoShowRooms.map((room) => room.roomName),
    topNoShowRooms.map((room) => room.officeName),
  );
  const noShowLabelColumn = leftAlignedLabels(noShowLabels, { minWidth: isMobile ? 60 : 90 });

  const noShowTrace: Plotly.Data = {
    x: topNoShowRooms.map((room) => room.noShows),
    y: noShowLabels,
    text: topNoShowRooms.map((room) => (room.officeName ? `${room.roomName} · ${room.officeName}` : room.roomName)),
    type: 'bar',
    orientation: 'h',
    marker: { color: DashboardTheme.red500 },
    hovertemplate: '%{text}<br>%{x} no-shows<extra></extra>',
  };

  const employeeChartTrace: Plotly.Data = {
    x: employeeChartRows.map((employee) => employee.presentDays),
    y: employeeChartRows.map((employee) => employee.fullName),
    text: employeeChartRows.map((employee) => employee.fullName),
    type: 'bar',
    orientation: 'h',
    marker: { color: DashboardTheme.teal500 },
    hovertemplate: '%{text}<br>%{x} days<extra></extra>',
  };

  // Edge cases (no data for this filter, transient fetch failure) render inline below the
  // controls instead of replacing the whole tab, so switching period/company/comparison after
  // a bad selection never requires a full page reload to recover.
  let reportingErrorMessageKey = 'reporting-error-message';
  if (errorStatus === 401) {
    reportingErrorMessageKey = 'reporting-no-access-for-selection';
  } else if (errorStatus === 400) {
    reportingErrorMessageKey = 'reporting-invalid-period-message';
  }

  return (
    <div>
      <div className={style(styles.controlsRow(isMobile))}>
        <div className={style(styles.controlGroup)}>
          <span className={style(styles.controlLabel)}>{t('reporting-period-label')}</span>
          <div className={style(styles.presetChips)}>
            {PRESETS.map((presetKey) => (
              <div
                key={presetKey}
                role="button"
                tabIndex={0}
                className={style(styles.chip(preset === presetKey))}
                onClick={(): void => setPreset(presetKey)}
                onKeyDown={(event): void => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    setPreset(presetKey);
                  }
                }}
              >
                {t(`reporting-preset-${presetKey}`)}
              </div>
            ))}
          </div>
          {preset === 'custom' && (
            <div className={style(styles.dateRangeRow)}>
              <span className={style(styles.controlLabel)}>{t('choose-the-start-day')}</span>
              <DatePicker
                calendarClassName={style(styles.calendar)}
                format="dd/MM/yyyy"
                value={customStart}
                onChange={(value: Value): void => setCustomStart(value instanceof Date ? value : undefined)}
                clearIcon={null}
              />
              <span className={style(styles.controlLabel)}>{t('choose-the-end-day')}</span>
              <DatePicker
                calendarClassName={style(styles.calendar)}
                format="dd/MM/yyyy"
                value={customEnd}
                onChange={(value: Value): void => setCustomEnd(value instanceof Date ? value : undefined)}
                clearIcon={null}
              />
            </div>
          )}
          {isCustomRangeInvalid && <span className={style(styles.errorMessage)}>{t('reporting-invalid-period-message')}</span>}
        </div>

        <div className={style(styles.controlGroup)}>
          <span className={style(styles.controlLabel)}>{t('reporting-comparison-label')}</span>
          <div className={style(styles.comparisonRow)}>
            {COMPARISON_MODES.map((mode) => (
              <div
                key={mode}
                role="button"
                tabIndex={0}
                className={style(styles.comparisonOption(comparison === mode))}
                onClick={(): void => setComparison(mode)}
                onKeyDown={(event): void => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    setComparison(mode);
                  }
                }}
              >
                {t(comparisonModeKey(mode))}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className={style(styles.exportRow)}>
        <PrimaryButton onClick={handleCsvExport} loading={isExportingCsv} disabled={isCustomRangeInvalid}>
          {t('reporting-export-csv')}
        </PrimaryButton>
        <PrimaryButton onClick={handlePdfExport} loading={isExportingPdf} disabled={isCustomRangeInvalid}>
          {t('reporting-export-pdf')}
        </PrimaryButton>

        {!compareMode && (
          <select
            className={style(styles.companySelect)}
            value={reportOfficeId ?? ''}
            onChange={(event): void => setReportOfficeId(event.target.value || undefined)}
          >
            <option value="">{t('reporting-all-offices')}</option>
            {compareCandidateOffices.map((office) => (
              <option key={office.id} value={office.id}>
                {office.name}
              </option>
            ))}
          </select>
        )}

        {isWhyellow && (
          <select
            className={style(styles.companySelect)}
            value={selectedCompanyId ?? ''}
            onChange={(event): void => setSelectedCompanyId(event.target.value || undefined)}
          >
            <option value="">{t('reporting-all-companies')}</option>
            {(companies ?? []).map((company) => (
              <option key={company.id} value={company.id}>
                {company.name}
              </option>
            ))}
          </select>
        )}

        <div
          role="button"
          tabIndex={0}
          className={style(styles.chip(compareMode))}
          onClick={(): void => setCompareMode((prev) => !prev)}
          onKeyDown={(event): void => {
            if (event.key === 'Enter' || event.key === ' ') {
              setCompareMode((prev) => !prev);
            }
          }}
        >
          {t('reporting-compare-offices-toggle')}
        </div>
      </div>

      {compareMode && (
        <div className={style(styles.compareOfficesPanel)}>
          <div className={style(styles.compareOfficesControlsRow)}>
            <span className={style(styles.controlLabel)}>{t('reporting-compare-offices-label')}</span>
          </div>
          {compareCandidateOffices.length === 0 && (
            <span className={style(tabStyles.emptyMessage)}>{t('reporting-compare-offices-no-offices')}</span>
          )}
          {compareCandidateOffices.length > 0 && (
            <div className={style(styles.officeCheckboxList)}>
              {compareCandidateOffices.map((office) => (
                <label key={office.id} className={style(styles.officeCheckboxRow)} htmlFor={`compare-office-${office.id}`}>
                  <input
                    id={`compare-office-${office.id}`}
                    type="checkbox"
                    checked={office.id !== undefined && selectedOfficeIds.includes(office.id)}
                    onChange={(): void => {
                      if (office.id) {
                        toggleCompareOffice(office.id);
                      }
                    }}
                  />
                  {office.name}
                </label>
              ))}
            </div>
          )}
          {selectedOfficeIds.length < 2 && <div className={style(styles.compareOfficesHint)}>{t('reporting-compare-offices-hint')}</div>}
        </div>
      )}

      {!initialized && (
        <Center>
          <ActivityIndicator size={20} color={StyleConstants.GREY_COLOR} />
        </Center>
      )}

      {initialized && (error || !reporting) && <span className={style(styles.errorMessage)}>{t(reportingErrorMessageKey)}</span>}

      {reporting && reporting.officeBreakdowns && reporting.officeBreakdowns.length > 0 && (
        <div className={style(styles.chartCard)}>
          <div className={style(styles.chartTitle)}>{t('reporting-office-comparison-title')}</div>
          <Plot
            data={reporting.officeBreakdowns.map((office: OfficeReportBreakdown, index: number) => ({
              x: office.occupancyTrend.map((_point: TrendPoint, pointIndex: number) => pointIndex),
              y: office.occupancyTrend.map((point: TrendPoint) => point.occupancyPercentage),
              text: office.occupancyTrend.map((point: TrendPoint) => point.date),
              hovertemplate: '%{text}<br>%{y:.1f}%<extra></extra>',
              type: 'scatter' as const,
              mode: 'lines+markers' as const,
              name: office.officeName,
              line: { color: OFFICE_COMPARISON_TREND_COLORS[index % OFFICE_COMPARISON_TREND_COLORS.length] },
            }))}
            layout={{
              autosize: true,
              height: 320,
              margin: { l: 40, r: 20, t: 10, b: 40 },
              font: { family: DashboardTheme.bodyFontFamily, color: DashboardTheme.gray700, size: 12 },
              hovermode: 'x unified',
              xaxis: { title: t('reporting-trend-xaxis-label').toString(), gridcolor: DashboardTheme.gray100, zeroline: false },
              yaxis: { title: '%', rangemode: 'tozero', gridcolor: DashboardTheme.gray100, zeroline: false },
              legend: { orientation: 'h', y: -0.25 },
              plot_bgcolor: 'rgba(0,0,0,0)',
              paper_bgcolor: 'rgba(0,0,0,0)',
              hoverlabel: PLOTLY_HOVERLABEL,
            }}
            style={{ width: '100%' }}
            useResizeHandler
            config={plotlyChartConfig('office-comparison')}
          />
          <div className={style(tabStyles.chartHint)}>
            <InfoOutlinedIcon className={style(tabStyles.chartHintIcon)} fontSize="inherit" />
            {t('dashboard-chart-zoom-hint')}
          </div>
          <div className={classes(styles.tableWrap, style({ marginTop: 16, marginBottom: 0 }))}>
            <table className={style(styles.table)}>
              <thead>
                <tr>
                  <th className={style(styles.tableHeaderCell)}>{t('reporting-table-office')}</th>
                  <th className={style(styles.tableHeaderCell)}>{t('reporting-kpi-avg-occupancy')}</th>
                  <th className={style(styles.tableHeaderCell)}>{t('reporting-kpi-reservations')}</th>
                  <th className={style(styles.tableHeaderCell)}>{t('reporting-table-checkins')}</th>
                  <th className={style(styles.tableHeaderCell)}>{t('reporting-kpi-no-shows')}</th>
                  <th className={style(styles.tableHeaderCell)}>{t('reporting-kpi-no-show-rate')}</th>
                  {hasOfficeGroupKpis && (
                    <>
                      <th className={style(styles.tableHeaderCell)}>{t('reporting-table-group-reservations')}</th>
                      <th className={style(styles.tableHeaderCell)}>{t('reporting-table-group-no-show-rate')}</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {reporting.officeBreakdowns.map((office: OfficeReportBreakdown) => (
                  <tr key={office.officeId}>
                    <td className={style(styles.tableCell)}>{office.officeName}</td>
                    <td className={style(styles.tableCell)}>{office.kpis.averageOccupancyPercentage.toFixed(1)}%</td>
                    <td className={style(styles.tableCell)}>{office.kpis.reservations}</td>
                    <td className={style(styles.tableCell)}>{office.kpis.checkIns}</td>
                    <td className={style(styles.tableCell)}>{office.kpis.noShows}</td>
                    <td className={style(styles.tableCell)}>{office.kpis.noShowRatePercentage.toFixed(1)}%</td>
                    {hasOfficeGroupKpis && (
                      <>
                        <td className={style(styles.tableCell)}>
                          {office.groupKpis?.reservations ?? 0}
                          {' / '}
                          {office.kpis.reservations}
                        </td>
                        <td className={style(styles.tableCell)}>{(office.groupKpis?.noShowRatePercentage ?? 0).toFixed(1)}%</td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {reporting && (
      <>
        <ReportingKpiRow
          kpis={reporting.kpis}
          comparisonKpis={reporting.comparisonKpis}
          comparison={comparison}
          title={reporting.groupKpis ? t('reporting-kpis-all').toString() : undefined}
          t={t}
        />
        {reporting.groupKpis && (
          <ReportingKpiRow
            kpis={reporting.groupKpis}
            comparisonKpis={reporting.comparisonGroupKpis}
            comparison={comparison}
            title={t('reporting-kpis-groups').toString()}
            totals={reporting.kpis}
            t={t}
          />
        )}

        <div className={style(styles.chartCard)}>
          <div className={style(styles.chartTitle)}>{t('reporting-trend-chart-title')}</div>
          <Plot
            data={trendTraces}
            layout={{
              autosize: true,
              height: 300,
              margin: { l: 40, r: 20, t: 10, b: 40 },
              font: { family: DashboardTheme.bodyFontFamily, color: DashboardTheme.gray700, size: 12 },
              hovermode: 'x unified',
              xaxis: { title: t('reporting-trend-xaxis-label').toString(), gridcolor: DashboardTheme.gray100, zeroline: false },
              yaxis: { title: '%', rangemode: 'tozero', gridcolor: DashboardTheme.gray100, zeroline: false },
              legend: { orientation: 'h', y: -0.25 },
              plot_bgcolor: 'rgba(0,0,0,0)',
              paper_bgcolor: 'rgba(0,0,0,0)',
              hoverlabel: PLOTLY_HOVERLABEL,
            }}
            style={{ width: '100%' }}
            useResizeHandler
            config={plotlyChartConfig('occupancy-trend')}
          />
          <div className={style(tabStyles.chartHint)}>
            <InfoOutlinedIcon className={style(tabStyles.chartHintIcon)} fontSize="inherit" />
            {t('dashboard-chart-zoom-hint')}
          </div>
        </div>

        <div className={style(styles.chartCard)}>
          <div className={style(styles.chartTitle)}>{t('reporting-presence-chart-title')}</div>
          {reporting.occupancyTrend.length === 0 ? (
            <span className={style(tabStyles.emptyMessage)}>{t('reporting-no-data')}</span>
          ) : (
            <Plot
              data={presenceTraces}
              layout={{
                autosize: true,
                height: 300,
                margin: { l: 40, r: 20, t: 10, b: 40 },
                font: { family: DashboardTheme.bodyFontFamily, color: DashboardTheme.gray700, size: 12 },
                hovermode: 'x unified',
                barmode: 'group',
                bargap: 0.3,
                xaxis: { title: t('reporting-trend-xaxis-label').toString(), gridcolor: DashboardTheme.gray100, zeroline: false },
                yaxis: {
                  title: t('reporting-presence-yaxis-label').toString(),
                  rangemode: 'tozero',
                  gridcolor: DashboardTheme.gray100,
                  zeroline: false,
                },
                legend: { orientation: 'h', y: -0.25 },
                plot_bgcolor: 'rgba(0,0,0,0)',
                paper_bgcolor: 'rgba(0,0,0,0)',
                hoverlabel: PLOTLY_HOVERLABEL,
              }}
              style={{ width: '100%' }}
              useResizeHandler
              config={plotlyChartConfig('presence')}
            />
          )}
          {reporting.occupancyTrend.length > 0 && (
            <div className={style(tabStyles.chartHint)}>
              <InfoOutlinedIcon className={style(tabStyles.chartHintIcon)} fontSize="inherit" />
              {t('dashboard-chart-zoom-hint')}
            </div>
          )}
        </div>

        <div className={style(styles.chartCard)}>
          <div className={style(styles.chartTitle)}>{t('reporting-no-show-chart-title')}</div>
          {topNoShowRooms.length === 0 ? (
            <span className={style(tabStyles.emptyMessage)}>{t('reporting-no-data')}</span>
          ) : (
            <Plot
              data={[noShowTrace]}
              layout={{
                autosize: true,
                height: Math.max(240, topNoShowRooms.length * 32),
                // Room names sit left-aligned in a column sized to the longest one (Plotly's own axis
                // labels can only be right-aligned against the bars), so none is cut off.
                margin: { l: noShowLabelColumn.marginLeft, r: 20, t: 10, b: 40 },
                font: { family: DashboardTheme.bodyFontFamily, color: DashboardTheme.gray700, size: 12 },
                xaxis: { gridcolor: DashboardTheme.gray100, zeroline: false },
                yaxis: { gridcolor: DashboardTheme.gray100, zeroline: false, type: 'category', showticklabels: false },
                annotations: noShowLabelColumn.annotations,
                plot_bgcolor: 'rgba(0,0,0,0)',
                paper_bgcolor: 'rgba(0,0,0,0)',
                hoverlabel: PLOTLY_HOVERLABEL,
              }}
              style={{ width: '100%' }}
              useResizeHandler
              config={plotlyChartConfig('top-no-show-rooms')}
            />
          )}
          {topNoShowRooms.length > 0 && (
            <div className={style(tabStyles.chartHint)}>
              <InfoOutlinedIcon className={style(tabStyles.chartHintIcon)} fontSize="inherit" />
              {t('dashboard-chart-zoom-hint')}
            </div>
          )}
        </div>

        <div className={styles.tableWrap}>
          <table className={style(styles.table)}>
            <thead>
              <tr>
                <SortableHeaderCell
                  className={style(styles.tableHeaderCell)}
                  label={t('reporting-table-room')}
                  sortKeyName="room"
                  activeSortKey={roomsSortKey}
                  sortDirection={roomsSortDirection}
                  onSort={toggleRoomsSort}
                />
                {showOfficeColumn && (
                <SortableHeaderCell
                  className={style(styles.tableHeaderCell)}
                  label={t('reporting-table-office')}
                  sortKeyName="office"
                  activeSortKey={roomsSortKey}
                  sortDirection={roomsSortDirection}
                  onSort={toggleRoomsSort}
                />
                )}
                <SortableHeaderCell
                  className={style(styles.tableHeaderCell)}
                  label={t('reporting-kpi-avg-occupancy')}
                  sortKeyName="avgOccupancy"
                  activeSortKey={roomsSortKey}
                  sortDirection={roomsSortDirection}
                  onSort={toggleRoomsSort}
                />
                <SortableHeaderCell
                  className={style(styles.tableHeaderCell)}
                  label={t('reporting-kpi-reservations')}
                  sortKeyName="reservations"
                  activeSortKey={roomsSortKey}
                  sortDirection={roomsSortDirection}
                  onSort={toggleRoomsSort}
                />
                <SortableHeaderCell
                  className={style(styles.tableHeaderCell)}
                  label={t('reporting-table-checkins')}
                  sortKeyName="checkIns"
                  activeSortKey={roomsSortKey}
                  sortDirection={roomsSortDirection}
                  onSort={toggleRoomsSort}
                />
                <SortableHeaderCell
                  className={style(styles.tableHeaderCell)}
                  label={t('reporting-kpi-no-shows')}
                  sortKeyName="noShows"
                  activeSortKey={roomsSortKey}
                  sortDirection={roomsSortDirection}
                  onSort={toggleRoomsSort}
                />
                <SortableHeaderCell
                  className={style(styles.tableHeaderCell)}
                  label={t('reporting-kpi-no-show-rate')}
                  sortKeyName="noShowRate"
                  activeSortKey={roomsSortKey}
                  sortDirection={roomsSortDirection}
                  onSort={toggleRoomsSort}
                />
              </tr>
            </thead>
            <tbody>
              {reporting.rooms.length === 0 && (
              <tr>
                <td className={style(styles.tableCell)} colSpan={showOfficeColumn ? 7 : 6}>
                  {t('reporting-no-data')}
                </td>
              </tr>
              )}
              {pagedReportRooms.map((room: RoomReport) => (
                <tr key={room.roomId}>
                  <td className={style(styles.tableCell)}>{room.roomName}</td>
                  {showOfficeColumn && <td className={style(styles.tableCell)}>{room.officeName}</td>}
                  <td className={style(styles.tableCell)}>{room.averageOccupancyPercentage.toFixed(1)}%</td>
                  <td className={style(styles.tableCell)}>{room.reservations}</td>
                  <td className={style(styles.tableCell)}>{room.checkIns}</td>
                  <td className={style(styles.tableCell)}>{room.noShows}</td>
                  <td className={style(styles.tableCell)}>{room.noShowRatePercentage.toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
          <TablePagination page={roomsPage} pageCount={roomsPageCount} onChangePage={setRoomsPage} />
        </div>

        {reporting.groups && (
          <GroupReportTable groups={reporting.groups} showOfficeColumn={showOfficeColumn} t={t} />
        )}
      </>
      )}

      <div className={style(styles.chartCard)}>
        <div className={style(styles.employeePresenceHeaderRow)}>
          <div className={style(styles.chartTitle)}>{t('reporting-employee-presence-title')}</div>
          <div className={style(styles.employeePresenceActions)}>
            <PrimaryButton onClick={handleEmployeeCsvExport} loading={isExportingEmployeeCsv} disabled={isCustomRangeInvalid}>
              {t('reporting-export-csv')}
            </PrimaryButton>
            <PrimaryButton onClick={handleEmployeePdfExport} loading={isExportingPdf} disabled={isCustomRangeInvalid}>
              {t('reporting-export-pdf')}
            </PrimaryButton>
          </div>
        </div>
        <div className={style(styles.employeePresenceControlsRow)}>
          <input
            type="text"
            className={style(tabStyles.searchInput, { maxWidth: 260 })}
            placeholder={t('search-by-name').toString()}
            value={employeeSearchQuery}
            onChange={(event): void => setEmployeeSearchQuery(event.target.value)}
          />
        </div>

        {isCustomRangeInvalid && <span className={style(styles.errorMessage)}>{t('reporting-invalid-period-message')}</span>}

        {!isCustomRangeInvalid && !employeesInitialized && (
          <Center>
            <ActivityIndicator size={20} color={StyleConstants.GREY_COLOR} />
          </Center>
        )}

        {employeesInitialized && employeesError && <span className={style(styles.errorMessage)}>{t('reporting-error-message')}</span>}

        {employeesInitialized && !employeesError && (
          <>
            {sortedEmployees.length === 0 ? (
              <span className={style(tabStyles.emptyMessage)}>{t('reporting-employee-presence-no-data')}</span>
            ) : (
              <Plot
                data={[employeeChartTrace]}
                layout={{
                  autosize: true,
                  height: Math.max(240, employeeChartRows.length * 32),
                  margin: { l: isMobile ? 110 : 160, r: 20, t: 10, b: 40 },
                  font: { family: DashboardTheme.bodyFontFamily, color: DashboardTheme.gray700, size: 12 },
                  xaxis: {
                    title: t('reporting-employee-presence-yaxis-label').toString(),
                    gridcolor: DashboardTheme.gray100,
                    zeroline: false,
                  },
                  // Widen the left margin to fit long names rather than cutting them off.
                  yaxis: { gridcolor: DashboardTheme.gray100, zeroline: false, automargin: true },
                  plot_bgcolor: 'rgba(0,0,0,0)',
                  paper_bgcolor: 'rgba(0,0,0,0)',
                  hoverlabel: PLOTLY_HOVERLABEL,
                }}
                style={{ width: '100%' }}
                useResizeHandler
                config={plotlyChartConfig('employee-presence')}
              />
            )}

            {sortedEmployees.length > 0 && (
              <div className={style(tabStyles.chartHint)}>
                <InfoOutlinedIcon className={style(tabStyles.chartHintIcon)} fontSize="inherit" />
                {t('dashboard-chart-zoom-hint')}
              </div>
            )}

            {sortedEmployees.length > 0 && (
              <div className={classes(styles.tableWrap, style({ marginTop: 16 }))}>
                <table className={style(styles.table)}>
                  <thead>
                    <tr>
                      <SortableHeaderCell
                        className={style(styles.tableHeaderCell)}
                        label={t('reporting-employee-presence-name')}
                        sortKeyName="fullName"
                        activeSortKey={employeesSortKey}
                        sortDirection={employeesSortDirection}
                        onSort={toggleEmployeesSort}
                      />
                      <SortableHeaderCell
                        className={style(styles.tableHeaderCell)}
                        label={t('reporting-employee-presence-email')}
                        sortKeyName="email"
                        activeSortKey={employeesSortKey}
                        sortDirection={employeesSortDirection}
                        onSort={toggleEmployeesSort}
                      />
                      <SortableHeaderCell
                        className={style(styles.tableHeaderCell)}
                        label={t('reporting-employee-presence-days')}
                        sortKeyName="presentDays"
                        activeSortKey={employeesSortKey}
                        sortDirection={employeesSortDirection}
                        onSort={toggleEmployeesSort}
                      />
                      <th
                        className={style(styles.tableHeaderCell, styles.rowActionCell)}
                        aria-label={t('reporting-employee-presence-detail-open').toString()}
                      />
                    </tr>
                  </thead>
                  <tbody>
                    {pagedEmployees.map((employee: EmployeePresence) => (
                      <tr key={employee.officeUserId}>
                        <td className={style(styles.tableCell)}>
                          <button
                            type="button"
                            className={style(styles.employeeNameButton)}
                            aria-label={t('reporting-employee-presence-detail-open').toString()}
                            onClick={(): void => setSelectedEmployee(employee)}
                          >
                            {employee.fullName}
                          </button>
                        </td>
                        <td className={style(styles.tableCell)}>{employee.email}</td>
                        <td className={style(styles.tableCell)}>{employee.presentDays}</td>
                        <td className={style(styles.tableCell, styles.rowActionCell)}>
                          <button
                            type="button"
                            className={style(styles.rowActionButton)}
                            aria-label={t('reporting-employee-presence-detail-open').toString()}
                            onClick={(): void => setSelectedEmployee(employee)}
                          >
                            <MoreHorizIcon fontSize="inherit" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <TablePagination page={employeesPage} pageCount={employeesPageCount} onChangePage={setEmployeesPage} />
              </div>
            )}
          </>
        )}
      </div>

      <EmployeePresenceDetailModal
        officeUserId={selectedEmployee?.officeUserId}
        fullName={selectedEmployee?.fullName ?? ''}
        email={selectedEmployee?.email ?? ''}
        startDate={startDate}
        endDate={endDate}
        filter={presenceFilter}
        handleClose={(): void => setSelectedEmployee(undefined)}
      />
    </div>
  );
};

export default ReportingTab;
